/**
 * Data hook for the asset "Lifecycle" tab: pages through the device history
 * (newest first) from `GET /customers/:customer/assets/:asset/lifecycle-events`.
 * Paging is keyset-based: the next page is requested with `before=<at of the
 * last loaded event>`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { authenticatedFetch } from "../../JWTAuthService";
import { configuration } from "../../Configuration";

export const LIFECYCLE_PAGE_SIZE = 50;

export type LifecycleEventSource = "app" | "portal" | "system" | "derived";

export interface LifecycleEventOperator {
  id?: string;
  email?: string;
  name?: string;
}

export interface LifecycleEvent {
  id: string;
  /** Known types are listed in `LIFECYCLE_EVENT_TYPES`; the server may send others. */
  type: string;
  /** ISO timestamp. */
  at: string;
  by: LifecycleEventOperator | null;
  source: LifecycleEventSource | string;
  details?: Record<string, unknown>;
}

export const LIFECYCLE_EVENT_TYPES = [
  "activated",
  "commissioned",
  "maintenanceStarted",
  "maintenanceEnded",
  "decommissioned",
  "reactivated",
  "sleep",
  "reset",
] as const;

export type LifecycleLoadError = "forbidden" | "notFound" | "generic";

interface LifecycleResponse {
  events: LifecycleEvent[];
  hasMore: boolean;
}

class LifecycleRequestError extends Error {
  kind: LifecycleLoadError;

  constructor(kind: LifecycleLoadError) {
    super(`lifecycle-events request failed (${kind})`);
    this.kind = kind;
  }
}

async function fetchPage(
  customer: string,
  assetId: string,
  types: readonly string[],
  before: string | undefined,
): Promise<LifecycleResponse> {
  const params = new URLSearchParams({ limit: String(LIFECYCLE_PAGE_SIZE) });
  if (before) params.set("before", before);
  if (types.length > 0) params.set("type", types.join(","));

  let response: any;
  try {
    response = await authenticatedFetch(
      `${configuration.serverUrl}/customers/${encodeURIComponent(
        customer,
      )}/assets/${encodeURIComponent(assetId)}/lifecycle-events?${params.toString()}`,
      { method: "GET" },
    );
  } catch {
    throw new LifecycleRequestError("generic");
  }

  if (!response?.ok) {
    if (response?.status === 403) throw new LifecycleRequestError("forbidden");
    if (response?.status === 404) throw new LifecycleRequestError("notFound");
    throw new LifecycleRequestError("generic");
  }

  try {
    const body = await response.json();
    return {
      events: Array.isArray(body?.events) ? body.events : [],
      hasMore: body?.hasMore === true,
    };
  } catch {
    throw new LifecycleRequestError("generic");
  }
}

export function useAssetLifecycleEvents(
  customer: string | undefined,
  assetId: string | undefined,
  types: readonly string[],
) {
  const [events, setEvents] = useState<LifecycleEvent[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<LifecycleLoadError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Drops responses of superseded requests (filter/asset change, retry).
  const generationRef = useRef(0);
  const typesKey = types.join(",");

  useEffect(() => {
    if (!customer || !assetId) return;
    const generation = ++generationRef.current;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setEvents([]);
    setHasMore(false);

    fetchPage(customer, assetId, typesKey ? typesKey.split(",") : [], undefined)
      .then((page) => {
        if (generation !== generationRef.current) return;
        setEvents(page.events);
        setHasMore(page.hasMore);
      })
      .catch((err: LifecycleRequestError) => {
        if (generation !== generationRef.current) return;
        setError(err.kind ?? "generic");
      })
      .finally(() => {
        if (generation === generationRef.current) setLoading(false);
      });
  }, [customer, assetId, typesKey, reloadToken]);

  const loadMore = useCallback(async () => {
    if (!customer || !assetId || loading || loadingMore || !hasMore) return;
    const last = events[events.length - 1];
    if (!last) return;
    const generation = generationRef.current;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await fetchPage(
        customer,
        assetId,
        typesKey ? typesKey.split(",") : [],
        last.at,
      );
      if (generation !== generationRef.current) return;
      setEvents((current) => [...current, ...page.events]);
      setHasMore(page.hasMore);
    } catch (err) {
      if (generation !== generationRef.current) return;
      setError((err as LifecycleRequestError).kind ?? "generic");
    } finally {
      if (generation === generationRef.current) setLoadingMore(false);
    }
  }, [customer, assetId, typesKey, loading, loadingMore, hasMore, events]);

  const retry = useCallback(() => setReloadToken((token) => token + 1), []);

  return { events, hasMore, loading, loadingMore, error, loadMore, retry };
}
